import {
  isMediaLibraryLoaded,
  isMediaLibraryQuotaLoaded,
  selectMediaLibraryFiles,
  selectMediaLibraryQuota,
  selectMediaLibraryTypes,
} from './../../../store/_features/media-library/media-library.selector';
import {
  addNewMediaLibraryFiles,
  MediaLibraryActions,
  MediaLibraryQuotaActions,
  MediaLibraryTypesActions,
} from './../../../store/_features/media-library/media-library.actions';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  LearmoModalRef,
  LearmoModalService,
} from '../../../../shared/services/learmo-modal.service';
import { Observable, Subscription, skipWhile } from 'rxjs';
import {
  MediaLibraryCategoryEnum,
  MediaLibraryFileModel,
  MediaLibraryTypeModel,
  MediaLibraryUploadType,
  UserQuotaModel,
} from 'src/app/models/sales-funnel';
import { UploadingFileProgressItem } from './media-library-upload-form/media-library-upload-form.component';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import {
  InOutAnimation,
  listAnimation,
} from 'src/app/shared/animations/animations';
import { Store } from '@ngrx/store';
import { UploadMetaData } from '../utils';

@Component({
  selector: 'app-media-library-modal',
  templateUrl: './media-library-modal.component.html',
  styleUrls: ['./media-library-modal.component.css'],
  animations: [
    trigger('shrinkOut', [
      state('in', style({})),
      transition('* => void', [
        style({ transform: 'translateX(0)', opacity: 1 }),
        animate(200, style({ transform: 'translateX(-150px)', opacity: 0 })),
      ]),
      transition('void => *', [
        style({ transform: 'translateX(-150px)', opacity: 0 }),
        animate(400, style({ transform: 'translateX(0)', opacity: 1 })),
      ]),
    ]),
    trigger('shrinkOutOnlyIn', [
      state('in', style({})),
      transition('void => *', [
        style({ transform: 'translateX(-150px)', opacity: 0 }),
        animate(400, style({ transform: 'translateX(0)', opacity: 1 })),
      ]),
    ]),
    InOutAnimation,
    listAnimation,
  ],
})
export class MediaLibraryModalComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  loaded: boolean = false;

  mediaStorage: MediaLibraryFileModel[][] = [];
  mediaLibraryFiles: MediaLibraryFileModel[] = [];
  mediaLibraryTypes: MediaLibraryTypeModel[] = [];
  filterFilesBy: string[] = [];
  mediaLibraryQuota: UserQuotaModel;
  isMediaLibraryQuotaLoaded: Observable<boolean>;

  @Input() uploadCategory: MediaLibraryCategoryEnum;
  @Input() selectedFiles: MediaLibraryFileModel[] = [];
  @Input() selectedFilesTemp: MediaLibraryFileModel[] = [];
  @Input() selectedFilesTempIds: number[] = [];
  @Input() selectedFilesIds: number[] = [];
  @Input() multiFiles = true;
  @Input() view = false;
  @Input() imgSpecs: {
    width?: number;
    height?: number;
    ratio?: number;
  } = {
    width: null,
    height: null,
    ratio: null,
  };
  @Input() uploadType: MediaLibraryUploadType | string =
    MediaLibraryUploadType.Media;
  @Input() maxFileSize: number;
  @Input() uploadMetaData: UploadMetaData;

  uploadingFiles: UploadingFileProgressItem[] = [];

  // Enhanced pagination
  startIndex = 0;
  lastIndex = 16;
  perPage = 8;
  partiallyLoading = false;

  @Output() selectedFilesChange: EventEmitter<MediaLibraryFileModel[]> =
    new EventEmitter<MediaLibraryFileModel[]>();
  @Output() selectedFilesIdsChange: EventEmitter<number[]> = new EventEmitter<
    number[]
  >();

  subscription: Subscription[] = [];

  _isModalOpen = false;
  @Input('isModalOpen') set isModalOpen(value: boolean) {
    if (!!value) {
      this._isModalOpen = true;
      this.open();
    } else {
      this._isModalOpen = false;
    }
  }

  @Output() isModalOpenChange = new EventEmitter<boolean>();

  @ViewChild('modalContent') modalContent: TemplateRef<any>;
  modalRef: LearmoModalRef;

  // Search functionality
  searchQuery: string = '';

  constructor(public modalService: LearmoModalService, private store: Store) {}
  ngAfterViewInit(): void {
    this.isMediaLibraryQuotaLoaded = this.store.select(
      isMediaLibraryQuotaLoaded
    );

    this.subscription.push(
      this.store
        .select(isMediaLibraryLoaded)
        .subscribe((v) => (this.loaded = v))
    );

    this.subscription.push(
      this.store.select(selectMediaLibraryFiles).subscribe((files) => {
        this.mediaLibraryFiles = files || [];
      })
    );

    this.subscription.push(
      this.store
        .select(selectMediaLibraryQuota)
        .pipe(skipWhile((v) => !v))
        .subscribe((q) => (this.mediaLibraryQuota = q))
    );

    this.subscription.push(
      this.store
        .select(selectMediaLibraryTypes)
        .pipe(skipWhile((value) => !value?.length))
        .subscribe((types) => {
          this.mediaLibraryTypes = [...types];

          const isMediaOnly = [
            MediaLibraryUploadType.Media,
            MediaLibraryUploadType.Image,
            MediaLibraryUploadType.Video,
            MediaLibraryUploadType.Audio,
            'media',
            'image',
            'video',
            'audio',
          ].includes(this.uploadType as any);

          if (!isMediaOnly && !this.mediaLibraryTypes.find((t) => t.name === 'other')) {
            this.mediaLibraryTypes.push({
              name: 'other',
              display_name: 'Other',
            });
          }
          if (
            !this.filterFilesBy?.length &&
            (this.uploadType == MediaLibraryUploadType.Media ||
              this.uploadType == MediaLibraryUploadType.Video ||
              this.uploadType == MediaLibraryUploadType.Audio ||
              this.uploadType == MediaLibraryUploadType.Image)
          ) {
            this.filterByType([this.uploadType as string]);
          } else {
            this.disableFilterByType();
          }
        })
    );
  }

  ngOnInit() {}

  ngOnDestroy() {
    this.subscription.forEach((s) => s.unsubscribe());
  }

  get isThereUploading(): boolean {
    return (
      Array.isArray(this.uploadingFiles) &&
      this.uploadingFiles.length > 0 &&
      this.uploadingFiles.some((x) => !!x?.name)
    );
  }

  get filteredFiles(): MediaLibraryFileModel[] {
    let files = this.mediaLibraryFiles || [];

    // Apply extension filter from uploadMetaData (Primary Filter)
    // if (this.uploadMetaData?.uploadExtensionsByType?.length) {
    //   const allowedExts = this.uploadMetaData.uploadExtensionsByType.map(ext => ext.toUpperCase());
    //   files = files.filter(f => {
    //     const fileExt = f.fileInfo?.ext?.toUpperCase();
    //     return allowedExts.includes(fileExt);
    //   });
    // }
    // // Fallback to type filter if no metadata extensions provided
    // else if (this.filterFilesBy?.length) {

    if (this.filterFilesBy?.length) {
      const knownTypes = this.mediaLibraryTypes
        .filter((t) => t.name !== 'other')
        .map((t) => t.name);

      files = files.filter((f) => {
        const fileTypeName = f.type?.name;
        
        // If the file's type is explicitly in the filter list, show it
        if (this.filterFilesBy.includes(fileTypeName)) return true;
        
        // If 'other' is selected, show files that DON'T match any of our known/registered types
        if (this.filterFilesBy.includes('other')) {
          return !knownTypes.includes(fileTypeName);
        }
        
        return false;
      });
    }

    // }

    // Apply search filter
    if (this.searchQuery?.trim()) {
      const query = this.searchQuery.toLowerCase().trim();
      files = files.filter(
        (f) =>
          f.fileInfo?.displayName?.toLowerCase().includes(query) ||
          f.fileInfo?.name?.toLowerCase().includes(query)
      );
    }

    return files;
  }

  async onOpenModal() {
    try {
      await this.getMediaLibraryTypes();
      await this.getMediaLibraryFiles();
      await this.getMediaLibraryQuota();

      if (Array.isArray(this.selectedFiles) && this.selectedFiles.length > 0) {
        this.selectedFilesTemp = this.selectedFiles.slice();
        this.selectedFilesTempIds = this.selectedFiles.map((f) => f.id);
      } else {
        this.resetMediaLibrary();
      }
    } catch (err) {
      console.error('Error opening media modal', err);
    }
  }

  open(content = this.modalContent) {
    this.onOpenModal();

    this.modalRef = this.modalService.open(content, {
      title: 'Media Library',
      premium: true,
      size: 'xl',
      centered: true,
      transparent: true, // Media library has its own design/backdrop preferences often
    });

    this.modalRef.result.finally(() => {
      this._isModalOpen = false;
      this.isModalOpenChange.emit(false);
      this.resetMediaLibrary();
      this.searchQuery = '';
    });
  }

  async getMediaLibraryFiles() {
    this.store.dispatch(
      MediaLibraryActions.load({
        category: this.uploadCategory,
        uploadType: this.uploadType,
      })
    );
  }

  async getMediaLibraryQuota() {
    this.store.dispatch(MediaLibraryQuotaActions.load());
  }

  async getMediaLibraryTypes() {
    this.store.dispatch(
      MediaLibraryTypesActions.load({ uploadType: this.uploadType })
    );
  }

  onUploadDone(uploadedFiles: MediaLibraryFileModel[]) {
    if (!Array.isArray(uploadedFiles) || uploadedFiles.length === 0) return;
    this.store.dispatch(addNewMediaLibraryFiles({ files: uploadedFiles }));

    if (this.multiFiles) {
      uploadedFiles.forEach((file) => this.toggleSelectFile(file));
    } else {
      this.selectedFilesTemp = [uploadedFiles[0]];
      this.selectedFilesTempIds = [uploadedFiles[0].id];
    }
  }

  filterByType(type: string[]) {
    this.filterFilesBy = Array.isArray(type) ? [...type] : [];
    this.resetPagination();
  }

  disableFilterByType() {
    const mediaTypes = ['image', 'video', 'audio'];

    if (this.uploadType === 'content' || this.uploadType === 'any') {
      this.filterFilesBy = [];
    } else if (mediaTypes.includes(this.uploadType as string)) {
      this.filterFilesBy = [this.uploadType as string];
    } else if (this.uploadType === 'media') {
      this.filterFilesBy = ['image', 'video'];
    } else {
      this.filterFilesBy = [];
    }
    this.resetPagination();
  }

  toggleSelectFile(file: MediaLibraryFileModel) {
    if (!file) return;
    if (!!this.multiFiles) this.toggleFileMultiFiles(file);
    else this.toggleFileSingleFile(file);
  }

  deselectFiles(files: MediaLibraryFileModel[]) {
    if (!Array.isArray(files) || files.length === 0) return;

    const idsToRemove = new Set(files.map((f) => f.id));
    this.selectedFilesTemp = this.selectedFilesTemp.filter(
      (f) => !idsToRemove.has(f.id)
    );
    this.selectedFilesTempIds = this.selectedFilesTemp.map((f) => f.id);
  }

  toggleFileSingleFile(file: MediaLibraryFileModel) {
    if (this.selectedFilesTempIds.includes(file.id)) {
      this.selectedFilesTemp = [];
      this.selectedFilesTempIds = [];
    } else {
      this.selectedFilesTemp = [file];
      this.selectedFilesTempIds = [file.id];
    }
  }

  toggleFileMultiFiles(file: MediaLibraryFileModel) {
    const idx = this.selectedFilesTempIds.indexOf(file.id);
    if (idx !== -1) {
      this.selectedFilesTemp.splice(idx, 1);
      this.selectedFilesTempIds.splice(idx, 1);
    } else {
      this.selectedFilesTemp.push(file);
      this.selectedFilesTempIds.push(file.id);
    }
  }

  doneSelectFiles() {
    this.selectedFiles = this.selectedFilesTemp.slice();
    this.selectedFilesIds = this.selectedFiles.map((v) => v.id);
    this.emitAllFiles();
    this.close();
  }

  onUnselectFile(itemId: number) {
    this.selectedFiles = this.selectedFiles.filter((f) => f.id !== itemId);
    this.selectedFilesIds = this.selectedFiles.map((f) => f.id);

    if (this.selectedFiles.length === 0) {
      this.resetMediaLibrary();
    }

    this.emitAllFiles();
  }

  emitAllFiles() {
    this.selectedFilesChange.emit(this.selectedFiles);
    this.selectedFilesIdsChange.emit(this.selectedFilesIds);
  }

  close() {
    if (this.modalRef) {
      this.modalRef.close();
    }
    this.resetMediaLibrary();
  }

  resetMediaLibrary() {
    this.selectedFilesTemp = [];
    this.selectedFilesTempIds = [];
  }

  resetPagination() {
    this.startIndex = 0;
    this.lastIndex = 16;
  }

  onScroll(): void {
    const totalFiles = this.filteredFiles?.length || 0;
    if (this.lastIndex < totalFiles) {
      if (this.partiallyLoading) return;
      this.partiallyLoading = true;
      setTimeout(() => {
        this.lastIndex = Math.min(this.lastIndex + this.perPage, totalFiles);
        this.partiallyLoading = false;
      }, 600);
    }
  }

  trackByMediaId(index: number, item: MediaLibraryFileModel) {
    return item?.id ?? index;
  }

  trackByUploadName(index: number, item: UploadingFileProgressItem) {
    return item?.name ?? index;
  }

  onSearchChange() {
    this.resetPagination();
  }
}
